import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataError,
  LoadingIndicator,
  SearchInput,
  Table,
  Typography
} from "@digital-services/ui";
import { Pagination } from "@ds-cps/ui";

import { OrganizationUser } from "@organization/models/OrganizationUser";
import { filterOrganizationUsers } from "@organization/services/filter";
import { sort } from "@organization/services/sort";
import { useGetOrganizationUsers } from "@organization/services/use-organization-users";
import DeleteOrganizationUserButton from "@users/organizationuser/components/DeleteOrganizationUserButton";
import { InviteOrganizationUserButton } from "@users/organizationuser/components/InviteOrganizationUserButton";

import { useSortQueryString } from "@hooks/useSortQueryString";
import { useUserContext } from "@hooks/useUserContext";

import { OrganizationUserDataRow } from "./organization-users-tab/OrganizationUserDataRow";
import { OrganizationUsersHeaderRow } from "./organization-users-tab/OrganizationUsersHeaderRow";

// TBD - refactor
// <OrganizationUsersTab> renders 6 times !!!
// TBD - refactor

export const OrganizationUsersTab: FunctionComponent = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "organization.users" });

  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [disableCheckedAll, setDisableCheckedAll] = useState<boolean>(false);
  const [checkedUsers, setCheckedUsers] = useState<OrganizationUser[]>([]);

  const [searchValue, setSearchValue] = useState<string | undefined>("");

  const { activeSort, sortDirection, handleSort } = useSortQueryString({
    defaultSort: "name",
    defaultDirection: "ascending"
  });

  const { organizationUsers, isLoading, error } = useGetOrganizationUsers();
  const [users, setUsers] = useState<OrganizationUser[] | undefined>(undefined);

  const { user: currentUser } = useUserContext();

  useEffect(() => {
    const filteredUsers = filterOrganizationUsers(
      organizationUsers,
      searchValue
    );
    const sortedUsers = sort(filteredUsers, activeSort, sortDirection);
    setUsers(sortedUsers);
  }, [organizationUsers, searchValue, activeSort, sortDirection]);

  useEffect(() => {
    if (users?.length) {
      // to avoid having checkbox "Select All" when ONLY one user is found
      setDisableCheckedAll(users.length === 1);
    } else {
      setDisableCheckedAll(true);
      return;
    }

    const predicate = (checkedUser: OrganizationUser) =>
      users.some(user => user.id === checkedUser.id);

    if (checkedUsers?.length > 0) {
      setCheckedUsers(prev => {
        return prev.filter(predicate);
      });
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  useEffect(() => {
    // IMPORTANT, `users` variable contains results after FILTER and after SORT and even after SEARCH,
    // Meaning that currentUser MAY NOT be in the list
    // And search result can be ONE entry in array or even NO RESULTS AT ALL

    // Modify checkedAll value depends on previous and next checked users considering also search results
    if (users && users.length > 0) {
      // is needed first time when row checked
      const hasCurrentUser = checkedUsers.some(u => u.id === currentUser.id);

      let val: boolean;
      if (!!searchValue && !hasCurrentUser && users.length === 1) {
        val = checkedUsers.length === users.length;
      } else {
        val = checkedUsers.length === users.length - 1;
      }

      setCheckedAll(val);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedUsers]);

  const toggleSelectAll = () => {
    setCheckedAll(prev => {
      const next = !prev;

      if (next) {
        const newChecked = users!.filter(u => u.id !== currentUser.id);
        setCheckedUsers(newChecked);
      } else {
        setCheckedUsers([]);
      }

      return next;
    });
  };

  const toggleRowCheckbox = (selectedUser: OrganizationUser) => {
    // We either filter those besides selected user
    // or we add new user for the first time into array

    setCheckedUsers(prev => {
      const exists = prev.some(u => u.id === selectedUser.id);

      const next = exists
        ? prev.filter(u => u.id !== selectedUser.id) // uncheck
        : [...prev, selectedUser]; // check

      return next;
    });
  };

  const resetChecked = () => {
    setCheckedUsers([]);
    setCheckedAll(false);
  };

  const handleSearchInput = useCallback(
    (newValue: string) => {
      // After start using `@digital-services/ui` when <SearchInput> is used with useSearchQueryString() hook, then such components goes into infinite rendering.
      // Turns out, that to use <SearchInput> properly, `onChange()` handler should be implemented as `useCallback()` instance.
      if (newValue !== searchValue) {
        setSearchValue(newValue);

        // This might be not good according React API
        if (checkedAll === true) {
          setCheckedAll(false);
        }
      }
    },

    [searchValue, checkedAll]
  );

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center gap-3">
        {checkedUsers.length > 0 ? (
          <div
            className="float-left flex items-center gap-1"
            data-testid="delete-users"
          >
            <Typography variant="smallTitle">
              {t("selected.message", { count: checkedUsers.length })}
            </Typography>
            {currentUser.isAdministrator ? (
              <DeleteOrganizationUserButton
                users={checkedUsers}
                onSuccess={resetChecked}
              />
            ) : null}
          </div>
        ) : null}

        <div className="grow"></div>

        {/* TBD  - extract Search and Invite button to dedicated component */}
        <div className="w-[500px]">
          <SearchInput
            data-testid="search-user"
            placeholder={t("search.placeholder")}
            onChange={handleSearchInput}
          />
        </div>

        {currentUser.isAdministrator && (
          <InviteOrganizationUserButton beforeDrawerOpen={resetChecked} />
        )}
      </div>

      <div data-testid="organization-users-table">
        <Table className="w-full" rowHoverHighlight>
          <thead>
            <OrganizationUsersHeaderRow
              activeSort={activeSort}
              sortDirection={sortDirection}
              handleSort={handleSort}
              checkedAll={checkedAll}
              disableCheckedAll={disableCheckedAll}
              toggleSelectAll={toggleSelectAll}
            />
          </thead>

          <tbody data-testid="tbody">
            {users?.map(organizationUser => (
              <OrganizationUserDataRow
                key={`organization-user-${organizationUser.id}`}
                organizationUser={organizationUser}
                isChecked={checkedUsers?.some(
                  u => u.id === organizationUser.id
                )}
                toggleRowCheckbox={toggleRowCheckbox}
              />
            ))}
          </tbody>
        </Table>

        <Pagination totalCount={users?.length} />
      </div>
    </>
  );
};
